import { useState, useEffect, useRef } from "react";
import { MessageCircle, X } from "lucide-react";
import { SUPPORT_NUMBERS } from "@/lib/constants";

const STORAGE_KEY = "sl_wa_seen";

export default function WhatsAppFab() {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const popoverRef = useRef(null);

  // Stop pulsing after first interaction (per session)
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) setPulse(false);
  }, []);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const toggle = () => {
    setOpen((o) => !o);
    setPulse(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  return (
    <div ref={popoverRef} className="fixed bottom-28 md:bottom-5 right-4 z-50 flex flex-col items-end gap-3">
      {/* Popover */}
      {open && (
        <div
          className="rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] border w-[300px] overflow-hidden animate-[sl-fadeup_0.2s_ease-out_both]"
          style={{ borderColor: "var(--sl-border)" }}
          data-testid="wa-popover"
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: "#075E54", color: "#fff" }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full grid place-items-center bg-white/15">
                <MessageCircle size={18} />
              </div>
              <div>
                <div className="font-semibold text-sm leading-tight">Swiftlink-Africa</div>
                <div className="text-[11px] text-white/70 leading-tight">Service client · 7j/7</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white p-1 -mr-1"
              aria-label="Fermer"
              data-testid="wa-close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-3 bg-[#ECE5DD]">
            <div className="bg-white rounded-xl rounded-tl-sm p-3 text-sm shadow-sm">
              👋 Bonjour ! Comment pouvons-nous vous aider ?
              <div className="text-[10px] text-gray-400 mt-1">Équipe Swiftlink-Africa</div>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 px-1">Choisissez votre pays</div>
            {SUPPORT_NUMBERS.map((s) => (
              <a
                key={s.country}
                href={s.waLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition border"
                style={{ borderColor: "var(--sl-border)" }}
                onClick={() => setOpen(false)}
                data-testid={`wa-option-${s.country.toLowerCase()}`}
              >
                <span className="text-2xl">{s.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{s.country}</div>
                  <div className="text-xs text-gray-500 font-mono">{s.phone}</div>
                </div>
                <MessageCircle size={16} className="text-[#25D366] flex-shrink-0" />
              </a>
            ))}
            <a
              href="/faq"
              className="block text-center text-xs text-[var(--sl-primary)] hover:underline pt-2 pb-1"
              onClick={() => setOpen(false)}
              data-testid="wa-faq-link"
            >
              Vous avez d'autres questions ? Consultez notre FAQ →
            </a>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={toggle}
        className="relative w-12 h-12 rounded-full grid place-items-center shadow-[0_8px_30px_rgba(37,211,102,0.45)] transition-transform hover:scale-105 active:scale-95"
        style={{ background: "#25D366" }}
        aria-label={open ? "Fermer le chat" : "Ouvrir le chat WhatsApp"}
        data-testid="wa-fab"
      >
        {pulse && !open && (
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "#25D366", opacity: 0.55 }} />
        )}
        {open ? (
          <X size={18} className="text-white relative z-10" />
        ) : (
          <svg viewBox="0 0 32 32" width="21" height="21" className="relative z-10" aria-hidden="true">
            <path
              fill="#FFFFFF"
              d="M16 4C9.4 4 4 9.4 4 16c0 2.1.5 4.1 1.5 5.9L4 28l6.3-1.5c1.7.9 3.7 1.5 5.7 1.5 6.6 0 12-5.4 12-12S22.6 4 16 4zm0 21.8c-1.8 0-3.6-.5-5.2-1.4l-.4-.2-3.7.9.9-3.6-.2-.4c-1-1.6-1.5-3.4-1.5-5.3 0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10zm5.6-7.5c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.7.1-.1.3-.3.4-.5.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5-.2 0-.4 0-.6 0-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.6 0 1.5 1.1 3 1.3 3.2.2.2 2.2 3.4 5.4 4.7 3.2 1.3 3.2.9 3.8.8.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.3z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}
